<template>
    <div class="custom-fields-container">
        <div class="main-content">
            <div class="header-and-controls">
                <h1>Volunteer Custom Fields</h1>
                <div class="top-controls">
                    <div class="mobile-group-select">
                        <select :value="activeSectionId" @change="setActiveSection($event.target.value)" aria-label="Select Group">
                            <option v-for="section in filteredSections" :key="section.id" :value="section.id">
                                {{ section.title }}
                            </option>
                        </select>
                    </div>
                    <div class="search-wrapper">
                        <udf-search v-model="searchQuery" />
                    </div>
                </div>
            </div>
            <div class="layout-container">
                <!-- Sidebar (Left Navigation) -->
                <udf-sidebar
                    :filtered-sections="filteredSections"
                    :active-section-id="activeSectionId"
                    :is-sorted="isSorted"
                    :is-searching="!!searchQuery"
                    @sort-groups="sortGroups"
                    @reset-sort="resetSort"
                    @set-active-section="setActiveSection"
                />

                <!-- Main Content Area -->
                <div class="content-area-wrapper">
                    <div v-if="displaySections.length > 0" class="content-area-scroll">
                        <div class="content-area-header">
                            <h3>Fields</h3>
                        </div>
                        <udf-section
                            v-for="section in displaySections"
                            :key="section.id"
                            :section="section"
                            :is-searching="!!searchQuery"
                            @cancel-section-edit="cancelSectionEdit"
                            @save-section-edit="saveSectionEdit"
                            @enable-section-edit="enableSectionEdit"
                            @enable-field-edit="enableFieldEdit"
                            @cancel-field-edit="cancelFieldEdit"
                            @save-field-edit="saveFieldEdit"
                            @handle-field-tab="handleFieldTab"
                            @handle-enter-key="handleEnterKey"
                            @toggle-section="toggleSection"
                            :ref="el => { if(el) sectionRefs[section.id] = el }"
                        />
                    </div>
                    <div class="content-area empty-state" v-else>
                        <div class="no-results-content">
                            <i class="fas fa-search"></i>
                            <p>No fields match your search.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    ref, computed, onMounted, nextTick, onUnmounted
} from 'vue';
import { INITIAL_DATA } from '../customFieldsMock.js';
import udfSidebar from './udfSidebar.vue';
import udfSection from './udfSection.vue';
import udfSearch from './udfSearch.vue';

export default {
    name: 'userDefinedFieldsViewer',
    components: {
        udfSidebar,
        udfSection,
        udfSearch
    },
    setup() {
        const sections = ref(JSON.parse(JSON.stringify(INITIAL_DATA))
            .map((sec, index) => ({
                ...sec,
                isEditing: false,
                isOpen: index === 0, // First section open by default
                toast: null,
                fields: sec.fields.map((f) => ({
                    ...f,
                    isEditing: false,
                    originalValue: f.value,
                    error: null
                }))
            })));

        const searchQuery = ref('');
        const isSorted = ref(false);
        let initialOrder = [];

        const sectionRefs = ref({});
        const activeSectionId = ref(sections.value.length ? sections.value[0].id : null);
        const editingSectionId = ref(null);
        const editingFieldId = ref(null);

        // Solo mode: only one group open at a time
        const soloMode = ref(true);

        const scrollToSection = (id) => {
            nextTick(() => {
                const el = document.getElementById('udf-section-' + id);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            });
        };

        const toggleSection = (sectionToToggle) => {
            const section = sections.value.find((s) => s.id === sectionToToggle.id);
            if (section) {
                if (searchQuery.value) {
                    // Always keep open if searching
                    section.isOpen = true;
                } else {
                    const willOpen = !section.isOpen;
                    if (willOpen && soloMode.value) {
                        sections.value.forEach((s) => {
                            if (s.id !== section.id) s.isOpen = false;
                        });
                    }
                    section.isOpen = willOpen;
                }

                // If it opened, optionally update the active section and scroll
                if (section.isOpen) {
                    activeSectionId.value = section.id;
                    scrollToSection(section.id);
                }
            }
        };

        const cancelSectionEdit = (sectionToEdit) => {
            const section = sections.value.find((s) => s.id === sectionToEdit.id);
            if (section) {
                section.isEditing = false;
                editingSectionId.value = null;
                section.fields.forEach((field) => {
                    field.value = field.originalValue;
                    field.isEditing = false;
                    field.error = null;
                });
            }
        };

        const cancelFieldEdit = (fieldToCancel, options = { focusSelf: true }) => {
            fieldToCancel.value = fieldToCancel.originalValue;
            fieldToCancel.isEditing = false;
            fieldToCancel.error = null;
            editingFieldId.value = null;
            if (options.focusSelf) {
                nextTick(() => {
                    const section = sections.value.find((s) => s.fields.some((f) => f.id === fieldToCancel.id));
                    if (section && sectionRefs.value[section.id]) {
                        sectionRefs.value[section.id].focusField(fieldToCancel.id);
                    }
                });
            }
        };

        const setActiveSection = (id) => {
            if (activeSectionId.value === id && !searchQuery.value) return;
            // Cancel edits before switching
            sections.value.forEach((s) => {
                if (s.isEditing) cancelSectionEdit(s);
                s.fields.forEach((f) => {
                    if (f.isEditing) cancelFieldEdit(f, { focusSelf: false });
                });
            });

            activeSectionId.value = id;
            if (searchQuery.value) {
                // Clicking a sidebar item while searching clears the search and takes you to that section
                searchQuery.value = '';
            }

            // Open the accordion section and scroll to it
            const targetSection = sections.value.find((s) => s.id === id);
            if (targetSection) {
                if (soloMode.value && !searchQuery.value) {
                    sections.value.forEach((s) => {
                        if (s.id !== targetSection.id) s.isOpen = false;
                    });
                }
                targetSection.isOpen = true;
                scrollToSection(id);
            }
        };

        const searchResults = computed(() => {
            if (!searchQuery.value) return [];
            const query = searchQuery.value.toLowerCase();
            return sections.value.map((section) => {
                const matchingFields = section.fields.filter((f) => {
                    const matchLabel = f.label.toLowerCase().includes(query);
                    const matchValue = f.value && String(f.value).toLowerCase().includes(query);
                    return matchLabel || matchValue;
                });

                if (matchingFields.length > 0) {
                    return {
                        ...section,
                        fields: matchingFields
                    };
                }
                return null;
            }).filter(Boolean);
        });

        const filteredSections = computed(() => {
            if (!searchQuery.value) return sections.value;
            // Sidebar will only show groups that have matching results
            return searchResults.value.map((s) => sections.value.find((orig) => orig.id === s.id));
        });

        const activeSection = computed(() => sections.value.find((s) => s.id === activeSectionId.value));

        const displaySections = computed(() => {
            if (searchQuery.value) {
                return searchResults.value;
            }
            return filteredSections.value; // Show all sections in the accordion
        });

        const sortGroups = () => {
            sections.value.sort((a, b) => a.title.localeCompare(b.title));
            isSorted.value = true;
        };

        const resetSort = () => {
            sections.value.sort((a, b) => initialOrder.indexOf(a.id) - initialOrder.indexOf(b.id));
            isSorted.value = false;
        };

        const focusNextField = (fromField) => {
            let activeFields = [];
            displaySections.value.forEach((s) => {
                activeFields = activeFields.concat(s.fields);
            });

            const fromFieldIndex = activeFields.findIndex((f) => f.id === fromField.id);

            if (fromFieldIndex !== -1 && fromFieldIndex + 1 < activeFields.length) {
                const nextField = activeFields[fromFieldIndex + 1];
                nextTick(() => {
                    const section = sections.value.find((s) => s.fields.some((f) => f.id === nextField.id));
                    if (section && sectionRefs.value[section.id]) {
                        sectionRefs.value[section.id].focusField(nextField.id);
                    }
                });
            }
        };

        const saveFieldEdit = async (fieldToSave, options = { focusNext: true }) => {
            const realField = fieldToSave;
            if (!realField) return;

            if (JSON.stringify(realField.value) === JSON.stringify(realField.originalValue)) {
                cancelFieldEdit(realField, { focusSelf: !options.focusNext });
                if (options.focusNext) {
                    focusNextField(realField);
                }
                return;
            }

            let hasValidationError = false;
            if (realField.type === 'email' && realField.value && !realField.value.includes('@')) {
                realField.error = 'Please enter a valid email format.';
                hasValidationError = true;
            } else {
                realField.error = null;
            }

            if (hasValidationError) {
                return;
            }

            try {
                await new Promise((resolve, reject) => {
                    setTimeout(() => {
                        if (realField.value === 'Server Error') {
                            reject(new Error('Simulated server error triggered by input.'));
                        } else {
                            resolve();
                        }
                    }, 500);
                });

                realField.originalValue = realField.value;
                realField.isEditing = false;
                editingFieldId.value = null;

                realField.toast = {
                    message: 'Field updated',
                    type: 'success'
                };
                setTimeout(() => {
                    realField.toast = null;
                }, 3000);

                if (options.focusNext) {
                    focusNextField(realField);
                }
            } catch (error) {
                realField.error = 'Could not save changes. Please try again.';
                realField.toast = {
                    message: 'Error saving field',
                    type: 'error'
                };
                setTimeout(() => {
                    realField.toast = null;
                }, 3000);
            }
        };

        const saveSectionEdit = async (sectionToEdit) => {
            const section = sections.value.find((s) => s.id === sectionToEdit.id);
            if (!section) return;

            let hasValidationError = false;
            section.fields.forEach((field) => {
                if (field.type === 'email' && field.value && !field.value.includes('@')) {
                    field.error = 'Please enter a valid email format.';
                    hasValidationError = true;
                } else {
                    field.error = null;
                }
            });

            if (hasValidationError) {
                return;
            }

            try {
                await new Promise((resolve, reject) => {
                    setTimeout(() => {
                        const hasServerError = section.fields.some((f) => f.value === 'Server Error');
                        if (hasServerError) {
                            reject(new Error('Simulated server error triggered by input.'));
                        } else {
                            resolve();
                        }
                    }, 500);
                });

                section.isEditing = false;
                editingSectionId.value = null;
                section.fields.forEach((field) => {
                    field.originalValue = field.value;
                    field.isEditing = false;
                });
                section.toast = {
                    message: 'Section updated!',
                    type: 'success'
                };
                setTimeout(() => {
                    section.toast = null;
                }, 3000);
            } catch (error) {
                section.toast = {
                    message: 'Error saving section.',
                    type: 'error'
                };
                setTimeout(() => {
                    section.toast = null;
                }, 3000);
            }
        };
        const enableSectionEdit = async (sectionToEdit) => {
            const section = sections.value.find((s) => s.id === sectionToEdit.id);
            if (section) {
                // Check if any other section or field has actual modified changes
                const isFieldModified = (f) => {
                    const val = f.value;
                    const orig = f.originalValue;
                    if (Array.isArray(val) && Array.isArray(orig)) {
                        return JSON.stringify([...val].sort()) !== JSON.stringify([...orig].sort());
                    }
                    return val !== orig;
                };

                const hasUnsavedChanges = sections.value.some((s) => s.isEditing && s.fields.some((f) => isFieldModified(f))) || sections.value.some((s) => s.fields.some((f) => f.isEditing && isFieldModified(f)));

                if (hasUnsavedChanges) {
                    const save = await new Promise((resolve) => {
                        library.showDialog(
                            'You are actively editing. Do you want to save your changes before proceeding?',
                            () => resolve(true),
                            () => resolve(false),
                            {
                                title: 'Unsaved Changes',
                                okButtonLabel: 'Save',
                                cancelButtonLabel: 'Discard',
                                preventCloseOnBackdropClick: true
                            }
                        );
                    });
                    if (save) {
                        // Save active edits
                        if (editingSectionId.value) {
                            const activeSection = sections.value.find((s) => s.id === editingSectionId.value);
                            if (activeSection) {
                                await saveSectionEdit(activeSection);
                                if (activeSection.isEditing) return; // Validation failed, stop proceeding

                                // Close the section after its success message clears (3s)
                                const sectionToClose = activeSection;
                                setTimeout(() => {
                                    sectionToClose.isOpen = false;
                                }, 3000);
                            }
                        } else if (editingFieldId.value) {
                            const activeField = sections.value.flatMap((s) => s.fields).find((f) => f.id === editingFieldId.value);
                            if (activeField) {
                                await saveFieldEdit(activeField, { focusNext: false });
                                if (activeField.isEditing) return; // Validation failed, stop proceeding

                                // Close the section containing the field after its success message clears (3s)
                                const sectionToClose = sections.value.find((s) => s.fields.some((f) => f.id === activeField.id));
                                if (sectionToClose) {
                                    setTimeout(() => {
                                        sectionToClose.isOpen = false;
                                    }, 3000);
                                }
                            }
                        }
                    } else {
                        // Discard changes, display "Canceled" feedback, and proceed
                        if (editingSectionId.value) {
                            const activeSection = sections.value.find((s) => s.id === editingSectionId.value);
                            if (activeSection) {
                                cancelSectionEdit(activeSection);
                                activeSection.toast = {
                                    message: 'Editing Mode Canceled',
                                    type: 'error'
                                };
                                setTimeout(() => {
                                    activeSection.toast = null;
                                    activeSection.isOpen = false;
                                }, 3000);
                            }
                        } else if (editingFieldId.value) {
                            const activeField = sections.value.flatMap((s) => s.fields).find((f) => f.id === editingFieldId.value);
                            if (activeField) {
                                cancelFieldEdit(activeField, { focusSelf: false });
                                const activeSection = sections.value.find((s) => s.fields.some((f) => f.id === activeField.id));
                                if (activeSection) {
                                    activeSection.toast = {
                                        message: 'Editing Mode Canceled',
                                        type: 'error'
                                    };
                                    setTimeout(() => {
                                        activeSection.toast = null;
                                        activeSection.isOpen = false;
                                    }, 3000);
                                }
                            }
                        }
                    }
                }

                // Cancel any edits anywhere since we either saved them or chose to discard (if no edits were active)
                sections.value.forEach((s) => {
                    if (s.isEditing) cancelSectionEdit(s);
                    s.fields.forEach((f) => {
                        if (f.isEditing) cancelFieldEdit(f, { focusSelf: false });
                    });
                    // Collapse other sections under soloMode
                    if (soloMode.value && s.id !== section.id) {
                        s.isOpen = false;
                    }
                });

                section.isEditing = true;
                section.isOpen = true; // Ensure the section expands when entering edit mode
                editingSectionId.value = section.id;
                editingFieldId.value = null;
                section.fields.forEach((f) => {
                    f.originalValue = f.value;
                    f.isEditing = true;
                });
            }
        };

        const enableFieldEdit = (fieldToEdit) => {
            // Cancel ANY editing section
            sections.value.forEach((s) => {
                if (s.isEditing) cancelSectionEdit(s);
            });
            // Cancel ANY other editing field across all sections
            sections.value.forEach((s) => {
                s.fields.forEach((f) => {
                    if (f.isEditing && f !== fieldToEdit) {
                        cancelFieldEdit(f, { focusSelf: false });
                    }
                });
            });

            // The passed fieldToEdit is the reactive object
            const realField = fieldToEdit;

            if (realField) {
                realField.isEditing = true;
                editingFieldId.value = realField.id;
                editingSectionId.value = null;
                nextTick(() => {
                    const section = sections.value.find((s) => s.fields.some((f) => f.id === realField.id));
                    if (section && sectionRefs.value[section.id]) {
                        sectionRefs.value[section.id].focusField(realField.id);
                    }
                });
            }
        };

        const handleEnter = (event, section) => {
            if (event.target.tagName === 'TEXTAREA') {
                return;
            }
            event.preventDefault();
            saveSectionEdit(section);
        };

        const handleLastFieldTab = (section, event) => {
            if (event.shiftKey) return;

            const container = event.currentTarget;
            const focusable = Array.from(container.querySelectorAll('input:not([type=hidden]), textarea, select, [tabindex]:not([tabindex="-1"])'));
            const lastFocusableElement = focusable[focusable.length - 1];

            if (event.target === lastFocusableElement) {
                event.preventDefault();
                if (sectionRefs.value[section.id]) {
                    sectionRefs.value[section.id].focusSaveButton();
                }
            }
        };

        const handleFieldTab = (section, currentIndex, event) => {
            const currentField = section?.fields[currentIndex];
            if (section.isEditing || !currentField?.isEditing) {
                return;
            }

            const container = event.currentTarget;
            const focusable = Array.from(container.querySelectorAll('input:not([type=hidden]), textarea, select, button, [tabindex]:not([tabindex="-1"])'));
            const currentFocusIndex = focusable.indexOf(event.target);
            const direction = event.shiftKey ? -1 : 1;

            if (direction === 1 && currentFocusIndex < focusable.length - 1) {
                return;
            }
            if (direction === -1 && currentFocusIndex > 0) {
                return;
            }

            const nextIndex = currentIndex + direction;

            if (nextIndex >= 0 && nextIndex < section.fields.length) {
                event.preventDefault();
                saveFieldEdit(currentField, { focusNext: false });

                if (currentField.error) {
                    nextTick(() => {
                        const sectionRef = sectionRefs.value[section.id];
                        if (sectionRef) sectionRef.focusField(currentField.id);
                    });
                    return;
                }

                const nextField = section.fields[nextIndex];
                enableFieldEdit(nextField);
            } else {
                saveFieldEdit(currentField);
            }
        };

        const handleEnterKey = (event, field, section) => {
            if (field.type === 'textarea' && event.shiftKey) {
                return;
            }
            event.preventDefault();
            if (section.isEditing) {
                saveSectionEdit(section);
            } else if (field.isEditing) {
                saveFieldEdit(field);
            }
        };

        const handleKeydown = (event) => {
            const isSaveShortcut = (event.ctrlKey || event.metaKey) && event.key === 's';

            if (event.key === 'Escape') {
                if (editingSectionId.value || editingFieldId.value) {
                    event.preventDefault();
                    if (editingSectionId.value) {
                        const sectionToCancel = sections.value.find((s) => s.id === editingSectionId.value);
                        if (sectionToCancel) {
                            cancelSectionEdit(sectionToCancel);
                        }
                    } else if (editingFieldId.value) {
                        let fieldToCancel = null;
                        sections.value.forEach((s) => {
                            const found = s.fields.find((f) => f.id === editingFieldId.value);
                            if (found) fieldToCancel = found;
                        });
                        if (fieldToCancel) {
                            cancelFieldEdit(fieldToCancel);
                        }
                    }
                }
                return;
            }

            if (isSaveShortcut) {
                event.preventDefault();
                if (editingSectionId.value) {
                    const sectionToSave = sections.value.find((s) => s.id === editingSectionId.value);
                    if (sectionToSave) saveSectionEdit(sectionToSave);
                } else if (editingFieldId.value) {
                    let fieldToSave = null;
                    sections.value.forEach((s) => {
                        const found = s.fields.find((f) => f.id === editingFieldId.value);
                        if (found) fieldToSave = found;
                    });
                    if (fieldToSave) saveFieldEdit(fieldToSave);
                }
            }
        };

        onMounted(() => {
            initialOrder = sections.value.map((s) => s.id);
            if (sections.value.length > 0) {
                activeSectionId.value = sections.value[0].id;
            }
            window.addEventListener('keydown', handleKeydown);
        });

        onUnmounted(() => {
            window.removeEventListener('keydown', handleKeydown);
        });

        return {
            sections,
            searchQuery,
            filteredSections,
            activeSection,
            displaySections,
            activeSectionId,
            isSorted,
            sectionRefs,
            setActiveSection,
            toggleSection,
            sortGroups,
            resetSort,
            enableSectionEdit,
            cancelSectionEdit,
            saveSectionEdit,
            enableFieldEdit,
            saveFieldEdit,
            cancelFieldEdit,
            handleEnter,
            handleLastFieldTab,
            handleFieldTab,
            handleEnterKey
        };
    }
};
</script>

<style scoped>
.custom-fields-container {
    font-family: var(--font-sans), "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
    //color: var(--grayscale-800);
}

.main-content {
    padding: var(--space-6) 2rem 2rem;
}

.header-and-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-6);
    flex-wrap: wrap;
    gap: var(--space-4);
    max-width: 1356px;
    width: 100%;
}

.main-content h1 {
    font-size: var(--text-size-h2);
    margin: 0;
}

button {
    border: none;
    background: none;
    cursor: pointer;
}

button:hover {
    color: var(--primary-800);
}

.layout-container {
    display: flex;
    gap: var(--space-8);
    align-items: flex-start;
    min-width: 0;
}

.top-controls {
    display: flex;
    flex: 1;
    justify-content: flex-end;
}

.search-wrapper {
    width: 100%;
    max-width: 400px;
}

.search-wrapper :deep(.search-container) {
    margin-bottom: 0;
}

.mobile-group-select {
    display: none;
}

.content-area-wrapper {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    width: 100%;
    max-width: 1040px;
}

.content-area-scroll {
    background: var(--surface-default);
    border-radius: var(--radius-lg);
    overflow: hidden;
}

.content-area-header {
    background: var(--grayscale-50);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--space-4) var(--space-6);
}

.content-area-header h3 {
    margin: 0;
    font-size: var(--text-size-lg);
    letter-spacing: 0.02rem;
    color: var(--grayscale-700);
    font-weight: var(--text-weight-semibold);
}

.empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
}

.no-results-content {
    text-align: center;
    color: var(--grayscale-400);
}

.no-results-content i {
    font-size: 3rem;
    margin-bottom: var(--space-4);
}

@media (max-width: 1024px) {
    .layout-container {
        flex-direction: column;
    }

    /* Hide the original sidebar on mobile */
    .layout-container > .sidebar {
        display: none !important;
    }

    .header-and-controls {
        flex-direction: column;
        align-items: stretch;
    }

    .top-controls {
        flex-direction: row;
        gap: var(--space-4);
        align-items: stretch; /* Make them equal height */
        justify-content: flex-start;
    }

    .mobile-group-select {
        display: block;
        min-width: 200px;
        flex: 1;
    }

    .mobile-group-select select {
        width: 100%;
        height: 100%; /* Fill the stretched height */
        padding: var(--space-3) var(--space-4);
        border: 1px solid var(--grayscale-300);
        border-radius: var(--radius-md);
        background: var(--surface-default);
        font-size: var(--text-size-md);
        color: var(--grayscale-800);
        cursor: pointer;
    }

    .mobile-group-select select:focus {
        outline: none;
        box-shadow: 0 0 0 2px var(--primary-500);
        border-color: transparent;
    }

    .search-wrapper {
        flex: 2;
        max-width: none;
    }
}
</style>
